import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store';
import { getApiUrl } from '../config/api.config';

const CENTRAL_SYNC_URL = process.env.REACT_APP_CENTRAL_SYNC_URL || 'http://5.75.156.170:3002/api';

export interface SyncData {
  storeId: string;
  timestamp: Date;
  data: {
    products?: any[];
    sales?: any[];
    inventory?: any[];
    users?: any[];
    locations?: any[];
    cash_drawer_sessions?: any[];
    transfers?: any[];
    commissions?: any[];
    advances?: any[];
    payroll_entries?: any[];
  };
}

export interface SyncResponse {
  success: boolean;
  message: string;
  timestamp: string;
  storeId?: string;
  recordsSent?: number;
  recordsReceived?: number;
}

export interface InitializeStoreResponse {
  success: boolean;
  message: string;
  recordsReceived: number;
  timestamp: string;
}

const baseQuery = fetchBaseQuery({
  baseUrl: '', // Empty base URL, we'll provide full URLs
  prepareHeaders: (headers, { getState }) => {
    console.log('🚨 SYNC prepareHeaders CALLED');
    const state = getState() as RootState;
    const token = state.auth.accessToken;
    console.log('🔍 Sync service auth debug:', {
      hasState: !!state,
      hasAuth: !!state.auth,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
      authState: state.auth
    });
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      console.log('✅ Token added to sync request headers');
    } else {
      console.log('❌ No token available for sync request');
    }
    return headers;
  },
});

// Custom base query that dynamically determines API URL (like main API)
const dynamicBaseQuery = async (args: any, api: any, extraOptions: any) => {
  // Construct full URL with current API base
  const apiBase = getApiUrl();
  console.log('🔍 Sync DynamicBaseQuery:', { apiBase, hostname: window.location.hostname, originalArgs: args });
  
  if (typeof args === 'string') {
    args = { url: `${apiBase}${args}` };
  } else if (args.url) {
    args = { ...args, url: `${apiBase}${args.url}` };
  }
  
  console.log('🔍 Sync Final request args:', args);
  
  // Debug: Check if we're about to make a POST request and what auth state looks like
  if (args.method === 'POST') {
    const state = api.getState() as any;
    console.log('🔍 Sync POST request debug - Auth state:', {
      hasAuth: !!state.auth,
      hasToken: !!state.auth?.accessToken,
      tokenPreview: state.auth?.accessToken ? `${state.auth.accessToken.substring(0, 20)}...` : 'none',
      fullAuth: state.auth
    });
  }
  
  return baseQuery(args, api, extraOptions);
};

export const syncApi = createApi({
  reducerPath: 'syncApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['Sync'],
  endpoints: (builder) => ({
    // Manual sync - send local changes to central
    pushDataToCentral: builder.mutation<SyncResponse, void>({
      query: () => ({
        url: '/sync/push',
        method: 'POST',
      }),
    }),

    // Manual sync - pull changes from central
    pullDataFromCentral: builder.mutation<SyncResponse, void>({
      query: () => ({
        url: '/sync/pull',
        method: 'POST',
      }),
    }),

    // Full manual sync (push then pull)
    performFullSync: builder.mutation<SyncResponse, void>({
      query: () => ({
        url: '/sync/full',
        method: 'POST',
      }),
    }),

    // Initialize store with all data from central
    initializeStoreFromCentral: builder.mutation<InitializeStoreResponse, void>({
      query: () => ({
        url: '/sync/initialize',
        method: 'POST',
      }),
    }),

    // Get sync status
    getSyncStatus: builder.query<{
      lastSync: string | null;
      syncEnabled: boolean;
      pendingChanges: number;
      status: string;
    }, void>({
      query: () => '/sync/status',
      providesTags: ['Sync'],
    }),

    // Get enhanced sync status with real record counts
    getEnhancedSyncStatus: builder.query<{
      lastSync: string | null;
      syncEnabled: boolean;
      pendingChanges: number;
      status: string;
      storeId: string;
      statistics: {
        totalRecords: number;
        recordsSent: number;
        recordsReceived: number;
        breakdown: {
          products: number;
          sales: number;
          inventory: number;
          users: number;
          commissions: number;
          transfers: number;
          cashDrawerSessions: number;
        };
      };
    }, void>({
      query: () => '/sync/status-enhanced',
      providesTags: ['Sync'],
    }),

    // Get sync history
    getSyncHistory: builder.query<{
      history: Array<{
        id: string;
        type: string;
        status: string;
        recordCount: number;
        createdAt: string;
        error?: string;
      }>;
    }, void>({
      query: () => '/sync/history',
      providesTags: ['Sync'],
    }),
  }),
});

export const {
  usePushDataToCentralMutation,
  usePullDataFromCentralMutation,
  usePerformFullSyncMutation,
  useInitializeStoreFromCentralMutation,
  useGetSyncStatusQuery,
  useGetSyncHistoryQuery,
  useGetEnhancedSyncStatusQuery,
} = syncApi;